/**
 * notificationService.js
 *
 * Purpose: Maestro API plumbing for in-app notifications (the header bell).
 * All rows are scoped to the caller — see /v1/notifications on the backend.
 */

import apiService, { API_BASE_URL } from './apiService';
import * as tokenStore from './tokenStore';

/** Returns { notifications, unreadCount } newest first. */
export async function fetchMyNotifications(token, { limit, unreadOnly } = {}) {
  const qs = new URLSearchParams();
  if (limit) qs.set('limit', String(limit));
  if (unreadOnly) qs.set('unreadOnly', 'true');
  const str = qs.toString();
  return apiService.get(`/v1/notifications/mine${str ? `?${str}` : ''}`, token);
}

export async function markNotificationRead(notificationId, token) {
  return apiService.post(`/v1/notifications/${notificationId}/read`, {}, token);
}

export async function markAllNotificationsRead(token) {
  return apiService.post('/v1/notifications/read-all', {}, token);
}

/** Clear (delete) one notification. */
export async function deleteNotification(notificationId, token) {
  return apiService.delete(`/v1/notifications/${notificationId}`, token);
}

/** Clear every notification for the caller. */
export async function clearAllNotifications(token) {
  return apiService.delete('/v1/notifications/mine', token);
}

// ── SSE push channel ──────────────────────────────────────────────────────────
// GET /v1/notifications/stream pushes { kind: "created" | "read" | "read-all" }
// events the moment they happen, replacing most of the bell's polling.
//
// Deliberately raw fetch() instead of authenticatedFetch: the response is a
// long-lived text/event-stream, not JSON. We can't use native EventSource
// either — it cannot send an Authorization header, and putting the JWT in the
// query string would leak it into request logs. fetch + ReadableStream sends
// the normal Bearer header and lets us parse SSE frames ourselves.
//
// The hosting platform (App Runner) cuts every request at ~120s; that is
// expected and handled by the reconnect loop below.

const STREAM_ENDPOINT = '/v1/notifications/stream';
const MAX_BACKOFF_MS = 30_000;

/** Best current token: the store's live one when valid, else the caller's. */
function streamToken(fallback) {
  const live = tokenStore.getAccessToken();
  return live && !tokenStore.isExpired() ? live : fallback;
}

/** Parse complete SSE frames out of `buffer`, invoking onEvent per frame. */
function drainSseBuffer(buffer, onEvent) {
  const frames = buffer.split('\n\n');
  const rest = frames.pop(); // trailing partial frame (or '')
  for (const frame of frames) {
    let eventName = 'message';
    const dataLines = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith(':')) continue; // heartbeat comment
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) continue;
    try {
      onEvent(eventName, JSON.parse(dataLines.join('\n')));
    } catch {
      // Malformed frame — skip; the fallback poll keeps state truthful.
    }
  }
  return rest;
}

/**
 * Open (and keep reopening) the notification event stream.
 *
 * onEvent(kind, payload) fires per server event; kinds are "connected",
 * "created" ({ notification }), "read" ({ notificationId, readAt }) and
 * "read-all" ({ readAt }). Returns a close() function — after calling it the
 * subscription is dead and will not reconnect.
 *
 * Reconnect policy: a server-side cut (the platform's ~120s limit) reconnects
 * almost immediately; network errors back off exponentially to 30s. A 401
 * goes through the shared single-flight token refresh and retries; if the
 * refresh fails the loop stops for good and the caller's fallback polling
 * takes over (which routes through apiService and performs the logout
 * redirect).
 */
export function subscribeToNotifications(token, onEvent) {
  let closed = false;
  let controller = null;
  let failures = 0;

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  (async () => {
    while (!closed) {
      try {
        controller = new AbortController();
        const response = await fetch(`${API_BASE_URL}${STREAM_ENDPOINT}`, {
          headers: {
            Authorization: `Bearer ${streamToken(token)}`,
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
        });

        if (response.status === 401) {
          // Rotate via the shared single-flight refresh and retry. If the
          // refresh fails the session is gone — stop for good and let the
          // fallback poll (which routes through apiService) do the logout.
          try {
            await tokenStore.refresh();
          } catch {
            return;
          }
          failures += 1;
          continue;
        }
        if (!response.ok || !response.body) {
          throw new Error(`Notification stream failed (${response.status})`);
        }

        failures = 0;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break; // server closed (platform cut) — reconnect below
          buffer = drainSseBuffer(
            buffer + decoder.decode(value, { stream: true }),
            onEvent
          );
        }
      } catch (err) {
        if (closed || err?.name === 'AbortError') return;
        failures += 1;
      }
      if (closed) return;
      // Clean cuts reconnect fast; repeated failures back off with jitter.
      const backoff =
        failures === 0
          ? 500
          : Math.min(MAX_BACKOFF_MS, 1000 * 2 ** failures) +
            Math.floor(Math.random() * 500);
      await sleep(backoff);
    }
  })();

  return () => {
    closed = true;
    controller?.abort();
  };
}
